import { useCallback, useMemo } from 'react'
import { bindActionCreators } from 'redux'
import { getSettings, removeSettings, setSettings } from '@cbgms/components/grid/state/actions'
import { useDispatch, useSelector } from 'react-redux'
import { ISettingsStoreParams } from '@cbgms/components/grid/Hooks/use-list-settings'
import { IColumn } from '@cbgms/components/grid/Types'

interface IFallbackReducerState {
  // IFallbackReducerState exists solely as a backup type definition, in case getUserId isn't properly passed
  auth: {
    employee: { UUID: string }
  }
}

export interface IUseListSettingsProps {
  /** Action to dispatch instead of the default settings load action */
  getSettings?: typeof getSettings

  /** Action to dispatch instead of the default settings store action */
  storeSettings?: typeof setSettings

  /** Action to dispatch instead of the default settings remove action */
  removeSettings?: typeof removeSettings

  getUserID?: () => string

  type?: string

  scope: string // passed down to list settings
  columns: IColumn[] // passed down to list settings hook
  predefinedSearch?: string // passed down to list settings hook
}
// useListSettingsProps: will return props required for the listSettings
// these are application specific and should be build depending on the application.
export const useListSettingsProps = (props: IUseListSettingsProps) => {
  const dispatch = useDispatch()

  // settings
  const connectedGetSettings = useCallback(bindActionCreators(props.getSettings || getSettings, dispatch), [props.getSettings, dispatch])
  // settings
  const connectedStoreSettings = useCallback(bindActionCreators(props.storeSettings || setSettings, dispatch), [
    props.storeSettings,
    dispatch
  ])
  // settings
  const connectedRemoveSettings = useCallback(bindActionCreators(props.removeSettings || removeSettings, dispatch), [
    props.removeSettings,
    dispatch
  ])
  const getUserID = useSelector((state: IFallbackReducerState) => {
    return props.getUserID ? props.getUserID : () => state.auth?.employee?.UUID
  })
  const settingsLoader = useCallback(async (): Promise<any> => {
    return connectedGetSettings(props.scope, props.type || 'list')
  }, [connectedGetSettings, props.scope, props.type])

  const settingsStore = useCallback(
    async (newSettings: ISettingsStoreParams): Promise<any> => {
      return connectedStoreSettings(props.scope, props.type || 'list', newSettings)
    },
    [connectedStoreSettings, props.scope, props.type]
  )

  const settingsRemover = useCallback(async (): Promise<any> => {
    return connectedRemoveSettings(props.scope, props.type || 'list')
  }, [connectedRemoveSettings, props.scope, props.type])

  return useMemo(
    () => ({
      settingsLoader,
      settingsStore,
      settingsRemover,
      getUserID,
      scope: props.scope,
      columns: props.columns,
      predefinedSearch: props.predefinedSearch
    }),
    [settingsLoader, settingsStore, settingsRemover, getUserID, props.scope, props.columns, props.predefinedSearch]
  )
}
